import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { EntityRecord } from '@/types/entity';
import { useSwivuChecklist, TUseSwivuChecklistReturn } from '@/react/hooks/entityHooks/useSwivuChecklist';
import { useEntityManagerContext } from '@/react/components/entityManager/context/EntityManagerContext';
import { useSwivuStore } from '@/react/hooks/useSwivuStore';
import { STORE_KEYS } from '@/other/constants/StoreKeys';
import { Checklist } from '@/bryntum-lib/models/Checklist';

interface EntityChecklistContextValue extends TUseSwivuChecklistReturn {
  entityRecord: EntityRecord;
  version: number;
}

const EntityChecklistContext = createContext<EntityChecklistContextValue | undefined>(undefined);

interface EntityChecklistProviderProps {
  children: ReactNode;
}

export const EntityChecklistProvider: React.FC<EntityChecklistProviderProps> = ({ children }) => {
  const { record } = useEntityManagerContext();
  const { version } = useSwivuStore<Checklist>({ storeId: STORE_KEYS.checklistStore });
  const checklistOperations = useSwivuChecklist({ record, version });

  const contextValue = useMemo(
    () => ({
      ...checklistOperations,
      entityRecord: record,
      version
    }),
    [checklistOperations, record, version]
  );

  return <EntityChecklistContext.Provider value={contextValue}>{children}</EntityChecklistContext.Provider>;
};

export const useEntityChecklistContext = (): EntityChecklistContextValue => {
  const context = useContext(EntityChecklistContext);
  if (!context) {
    throw new Error('useEntityChecklistContext must be used within EntityChecklistProvider');
  }
  return context;
};